import {
  AfterViewInit,
  ChangeDetectorRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChild,
  ViewChildren,
  Component,
  ChangeDetectionStrategy,
  AfterViewChecked,
  HostListener,
} from "@angular/core";
import { MatPaginator, PageEvent } from "@angular/material/paginator";
import { MatSort, MatSortHeader, SortDirection } from "@angular/material/sort";
import { merge, Subscription, Observable, fromEvent } from "rxjs";
import { debounceTime, tap } from "rxjs/operators";
import { DataTableFilter } from "../../components/data-table/filters/data-table-filter";
import { DataTableFilterDirective } from "../../directives/data-table-filter.directive";
import { FilterValueType } from "../../components/data-table/filters/dropdown-filter/dropdown-data-table-filter.component";
import {
  asArray,
  asBoolArray,
  FilterType,
  Bool,
  Status,
  HttpMethod,
} from "../../enum";
import {
  DataTableDataSource,
  Group,
  IGroupByColumn,
} from "../../_datasources/data-table.datasource";
import { DataTableFilterODataSerializer } from "../../_helpers/data-table-filter-serializer";
import {
  DataTableService,
  ODataResponse,
} from "../../_services/data-table.service";
import { ITableParams } from "./table-params";
import { IReactiveFormsDeepMappingOptions } from "src/app/util/form-util";
import { MatTable } from "@angular/material/table";
import { PAGE_SIZE } from "src/app/config";
import { LxmMessage } from "src/app/_helpers";

@Component({
  template: "",
}) // Necessary for proper injection
export class DataTableComponent<TItem, TData>
  implements OnInit, AfterViewInit, AfterViewChecked {
  // for use in template
  public FilterType = FilterType;
  public FilterValueType = FilterValueType;
  public Bool = Bool;
  public Status = Status;
  public trueFalse: any[] = asBoolArray(Bool);
  public statuses: any[] = asArray(Status);

  private scrollEvent$;
  public scrollContainerId: string;
  public shrinkOnScroll = true;

  protected loadAfterViewInit = false;

  private _dataSource: DataTableDataSource<TItem, TData>;

  private _serializer = new DataTableFilterODataSerializer();

  private _isInitialized: boolean;

  private _filters: DataTableFilter[];
  private _sort: MatSort;

  private _onUpdate = new EventEmitter();
  private _onRowClick = new EventEmitter<TItem>();

  protected colPropertyMap: any;

  @Input() public paginator: MatPaginator;

  @ViewChild("table") tableRef: MatTable<any>;

  @ViewChildren(MatSort)
  protected sorts: QueryList<MatSort>;

  @ViewChildren(DataTableFilterDirective)
  protected filterDirectives: QueryList<DataTableFilterDirective>;

  @Input() public displayedColumns: string[];

  @Input() public dataUrl: string;

  @Input()
  public method = HttpMethod.Get;

  @Input()
  public message: LxmMessage;

  @Input()
  public initialData: any;

  @Input()
  public customFilter: string;

  @Input() public orderBy: string;

  @Input() public forcedOrderBy: string;

  @Input() public pageIndex: number = 0;
  @Input() public pageSize: number = PAGE_SIZE.DEFAULT;

  @Input() public formMappingOptions: IReactiveFormsDeepMappingOptions;

  @Input() public groupBy: IGroupByColumn[];

  @Output()
  public onUpdate$ = this._onUpdate.asObservable();

  @Output()
  public onRowClick$ = this._onRowClick.asObservable();

  public loadOnInit = true;

  public get dataSource(): DataTableDataSource<TItem, TData> {
    return this._dataSource;
  }

  public get hasData() {
    return this.dataSource?.data?.length > 0 ? true : false;
  }

  private _filterSubscription: Subscription;
  private _sortSubscription: Subscription;
  private _sortStateSubscription: Subscription;
  private _pageSizeSubscription: Subscription;
  private _pageIndexSubscription: Subscription;
  private _pageLoadSubscription: Subscription;
  private _countSubscription: Subscription;
  private _dataSubjectSubscription: Subscription;

  private _scrollDataSubscription: Subscription;
  private _lastScrollCheck = 0;
  private _scrollCheckInterval = 100; // ms between scroll checks
  private _footerLoadingSubscription: Subscription;
  
  private _footer: any; // Reference to footer component

  constructor(
    private _dataService: DataTableService<TItem, TData>,
    public _cd: ChangeDetectorRef
  ) { }

  public detectChanges() {
    this._cd.detectChanges();
  }

  public hookPaginator(event: { paginator: MatPaginator; footer: any }) {
    const { paginator, footer } = event;
    
    this.paginator = paginator;
    this.pageIndex = paginator.pageIndex;
    this._footer = footer;
    
    if (!footer) return;
    
    if (this.dataUrl) {
      footer.setDataUrl(this.dataUrl);
      
      const savedState = this._dataService.getTableState(this.dataUrl);
      if (savedState?.orderBy) {
        this.orderBy = savedState.orderBy;
      }
    }
    
    if (this._dataSource) {
      if (this._footerLoadingSubscription) {
        this._footerLoadingSubscription.unsubscribe();
      }
      this._footerLoadingSubscription = this._dataSource.loading$.subscribe(isLoading => {
        footer.isLoading = isLoading;
      });
    }
  }

  public ngOnInit(): void {
    this._dataSource = new DataTableDataSource<TItem, TData>(
      this._dataService,
      this.method,
      this.message
    );

    if (this.loadOnInit) {
      this.load();
    }
  }

  public ngAfterViewInit(): void {
    this._bindFilters();
    this._bindSorts();

    if (this.paginator) {
      this._setupPaginator();
    }

    if (this.dataUrl && this._sort) {
      const savedState = this._dataService.getTableState(this.dataUrl);
      if (savedState?.orderBy) {
        this._restoreSortFromOrderBy(savedState.orderBy);
      }
    }

    if (this.scrollContainerId) {
      this.initScrollData(this.scrollContainerId);
    }

    this._isInitialized = true;
    if (this.loadAfterViewInit) {
      this.load();
    }

    this.detectChanges();
  }

  private _setupPaginator() {
    if (!this.paginator) return;

    if (this.pageSize && this.scrollContainerId) {
      this.paginator.pageSize = this.pageSize;
    }

    this._pageLoadSubscription = this.paginator.page.subscribe(
      (pageEvent) => {
        this.pageIndex = pageEvent.pageIndex;
        this.pageSize = pageEvent.pageSize;
        this.load();
      }
    );

    this._countSubscription = this._dataSource.count$.subscribe(
      (count) => {
        this.paginator.length = count;
      }
    );

    if (this._pageIndexSubscription) { this._pageIndexSubscription.unsubscribe(); }
    if (this._pageSizeSubscription) { this._pageSizeSubscription.unsubscribe(); }

    this._sortStateSubscription = this._dataSource.sort$.subscribe((x) => {
      if (x) {
        var segments = x.split(" ");
        this._sort.active = segments[0];
        this._sort.direction = (segments[1] || "") as SortDirection;

        const toState = "active";
        const colId = this.getColId(segments[0]);
        const col = this._sort.sortables.get(colId) as MatSortHeader;
        col._setAnimationTransitionState({ toState });
      }
    });

    if (this.scrollContainerId) {
      this._dataSubjectSubscription = this._dataSource.data$.subscribe(
        (data) => {
          const nonGroupItemsCount = data.filter(
            (item) => !(item instanceof Group)
          ).length;
          this.paginator.pageSize = nonGroupItemsCount;
          this.detectChanges();
        }
      );
    }
  }

  /**
   * Convert property name to column ID (reverse of colPropertyMap)
   * Used when restoring sort state from orderBy string
   */
  protected getColId(propertyName: string): string {
    if (!this.colPropertyMap) {
      return propertyName;
    }

    // Find the column ID that maps to this property name
    for (const [colId, propName] of Object.entries(this.colPropertyMap)) {
      if (propName === propertyName) {
        return colId;
      }
    }

    return propertyName;
  }

  public ngAfterViewChecked(): void {
    if (!this._isInitialized) {
      return;
    }

    this._bindSorts();
    this._bindFilters();
  }

  public load(initialData: any = this.initialData, onSuccess = () => { }, isScrollData = false): void {
    if (!this._dataSource) {
      return;
    }

    if (initialData) {
      this._dataSource.setData(
        initialData,
        this.formMappingOptions,
        this.groupBy,
      );
      this.initialData = null;
    } else {
      if (this.paginator && this.paginator.pageIndex !== this.pageIndex) {
        this.pageIndex = this.paginator.pageIndex;
      }

      const combinedOrderBy = this.getCombinedOrderBy();

      if (this.pageSize && this.scrollContainerId) {
        this.paginator.pageSize = this.pageSize;
      }

      this._dataSource.loadData(
        this.dataUrl,
        this.getData(),
        this.getTableParams(this.pageIndex),
        this.formMappingOptions,
        this.groupBy,
        () => {
          if (this.dataUrl && !isScrollData) {
            const pageSizeKey = this._footer?.localStorageKey;
            this._dataService.saveTableState(this.dataUrl, this.pageIndex, combinedOrderBy, pageSizeKey);
          }
          onSuccess();
        },
        isScrollData,
      );
    }

    this._onUpdate.emit();
  }

  public updateDataSourceData() {
    this._dataSource.updateData(
      this.dataSource.data,
      this.formMappingOptions,
      this.groupBy,
    );
    this.detectChanges();
  }

  public pushItem(item) {
    this._dataSource.pushItem(item, this.formMappingOptions, this.groupBy);
  }

  protected getData(): any {
    return this._dataSource.data;
  }

  // Grouping

  groupHeaderClick(row) {
    row.expanded = !row.expanded;
    const criteria = row.groupFor;
    const searchTerm = row[criteria];

    if (this.tableRef) {
      this.tableRef.updateStickyColumnStyles();
      this.tableRef.updateStickyHeaderRowStyles();
    }
    this.detectChanges();
  }

  isGroup(index, item): boolean {
    return item.level ? true : false;
  }

  //

  protected getTableParams(
    pageIndex = this._getPageIndexParam(),
    pageSize = this._getPageSizeParam(),
  ): ITableParams {
    const userSort = this.getSortParam(this._sort);
    const combinedSort = this.forcedOrderBy
      ? (userSort ? `${this.forcedOrderBy}, ${userSort}` : this.forcedOrderBy)
      : userSort;

    return {
      filter: this._getFilterParam(),
      sort: combinedSort,
      pageIndex: pageIndex,
      pageSize: pageSize,
      count: this._getCountParam(),
    };
  }

  protected onRowClick(row): void {
    this._onRowClick.emit(row);
  }

  protected getSortParam(sort: MatSort): string {
    if (!sort || !sort.active || sort.direction === "") {
      return "";
    }

    let prop = sort.active;
    if (
      this.colPropertyMap &&
      this.colPropertyMap.hasOwnProperty(sort.active)
    ) {
      prop = this.colPropertyMap[sort.active];
    }

    return prop + " " + sort.direction;
  }

  protected getCombinedOrderBy(): string {
    const currentSort = this.getSortParam(this._sort);
    return this.forcedOrderBy
      ? (currentSort ? `${this.forcedOrderBy}, ${currentSort}` : this.forcedOrderBy)
      : currentSort;
  }

  /**
   * Restore sort state from orderBy string (e.g., "name desc" or "forcedSort, name desc")
   */
  private _restoreSortFromOrderBy(orderBy: string): void {
    if (!orderBy || !this._sort) {
      return;
    }

    let userSort = orderBy;
    if (this.forcedOrderBy && orderBy.startsWith(this.forcedOrderBy)) {
      userSort = orderBy.substring(this.forcedOrderBy.length).trim();
      if (userSort.startsWith(',')) {
        userSort = userSort.substring(1).trim();
      }
    }

    if (!userSort) {
      return;
    }

    const segments = userSort.split(" ");
    if (segments.length >= 1) {
      const sortColumn = segments[0];
      const sortDirection = (segments[1] || "asc") as SortDirection;

      const colId = this.getColId(sortColumn);
      this._sort.active = colId;
      this._sort.direction = sortDirection;

      const col = this._sort.sortables.get(this._sort.active) as MatSortHeader;
      if (col) {
        col._setAnimationTransitionState({ toState: "active" });
      }
    }
  }

  private _getFilterParam(): string {
    if (this.customFilter) {
      return this.customFilter;
    }

    if (!this._filters) {
      return "";
    }

    return this._serializer.serialize(this._filters, this.colPropertyMap);
  }

  private _getPageIndexParam(): number {
    return this.paginator ? this.paginator.pageIndex : 0;
  }

  private _getPageSizeParam(): number {
    return this.paginator ? this.paginator.pageSize : null;
  }

  private _getCountParam(): boolean {
    return this.paginator ? true : false;
  }

  private _bindReload(...events: Observable<any>[]): Subscription {
    return merge(...events)
      .pipe(
        tap(() => {
          if (this.scrollContainerId) {
          this.resetPaginator();
          }
          this.load(null, () => {
            if (this.scrollContainerId) {
              document.getElementById(this.scrollContainerId).scrollTop = 0;
            }
          });
        }),
      )
      .subscribe();
  }

  private _bindSorts(): void {
    if (this._sortSubscription) {
      this._sortSubscription.unsubscribe();
    }

    if (!this.sorts || !this.sorts.first) {
      return;
    }

    this._sort = this.sorts.first;
    this._sortSubscription = this._sort.sortChange.pipe(
      tap(() => {
        this.load(null, () => {
          if (this.scrollContainerId) {
            document.getElementById(this.scrollContainerId).scrollTop = 0;
          }
        });
      })
    ).subscribe();
  }

  private _bindFilters(): void {
    if (this._filterSubscription) {
      this._filterSubscription.unsubscribe();
    }

    if (!this.filterDirectives) {
      return;
    }

    this._filters = this.filterDirectives.map((x) => x.compRef.instance);
    const filterEvents = this._filters.map((x) => x.onChange);
    this._filterSubscription = this._bindReload(...filterEvents);
  }

  protected clearSorts() {
    this._sort = undefined;
  }

  protected clearFilters() {
    this._filters = undefined;
  }

  public resetPaginator(): void {
    if (!this.paginator) {
      return;
    }

    this.pageIndex = 0;
    this.paginator.pageIndex = 0;
    
    // Clear stored state and notify footer to clear query params
    if (this.dataUrl) {
      this._dataService.clearTableState(this.dataUrl);
    }
  }

  // Reset pagination back to the first page without wiping table state.
  public resetToFirstPage(preserveSort = true): void {
    this.pageIndex = 0;

    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }

    if (this.dataUrl) {
      const pageSizeKey = this._footer?.localStorageKey;
      const orderBy = preserveSort ? this.getCombinedOrderBy() : '';
      this._dataService.saveTableState(this.dataUrl, 0, orderBy, pageSizeKey);
    }
  }

  // SCROLL DATA
  public loadScrollData(
    onSuccess = () => { },
  ): boolean {
    const itemsToInView = this.dataSource.data.filter(x => !(x instanceof Group)).length;
    const count = this.dataSource.count;

    if (count > 0 && itemsToInView >= count) {
      return false;
    }

    const nextPageIndex = this.pageIndex + 1;

    this._dataSource.loadData(
      this.dataUrl,
      this.getData(),
      this.getTableParams(nextPageIndex, this.pageSize),
      this.formMappingOptions,
      this.groupBy,
      () => {
        this.pageIndex = nextPageIndex;
        onSuccess();
      },
      true,
    );

    return true;
  }

  public onViewScroll(e) {
    const now = Date.now();
    // Throttle scroll checks to prevent too frequent calculations
    if (now - this._lastScrollCheck < this._scrollCheckInterval) {
      return;
    }
    this._lastScrollCheck = now;

    const viewHeight = e.target.offsetHeight;
    const scrollHeight = e.target.scrollHeight;
    const scrollLocation = e.target.scrollTop;

    const scrollPercentage = (scrollLocation + viewHeight) / scrollHeight;

    const isNearBottom = scrollPercentage > 0.6;

    if (isNearBottom && !this.dataSource.isLoading) {
      // When using scrollbar, we might jump large distances
      // So we'll load multiple pages if we're very close to bottom
      const pagesToLoad = scrollPercentage > 0.9 ? 2 : 1;

      const loadNextPage = (remainingPages) => {
        if (remainingPages <= 0 || this.dataSource.isLoading) return;

        this.loadScrollData(() => {
          // If we still need more pages and we're still near bottom, load another
          if (remainingPages > 1) {
            setTimeout(() => {
              const currentScrollPercentage = (e.target.scrollTop + e.target.offsetHeight) / e.target.scrollHeight;
              if (currentScrollPercentage > 0.8) {
                loadNextPage(remainingPages - 1);
              }
            }, 100);
          }
        });
      };

      loadNextPage(pagesToLoad);
    }

    this.detectChanges();
  }

  public initScrollData(scrollContainer: string = this.scrollContainerId) {
    if (this.scrollEvent$) {
      this.scrollEvent$.unsubscribe();
    }

    if (this._scrollDataSubscription) {
      this._scrollDataSubscription.unsubscribe();
    }

    setTimeout((_) => {
      const scrollElement = document.getElementById(scrollContainer);
      scrollElement.removeEventListener('scroll', this.onViewScroll);

      this.scrollEvent$ = fromEvent(scrollElement, 'scroll')
        .pipe(debounceTime(50))
        .subscribe((e: any) => {
          this.onViewScroll(e);
        });
    });
  }

  // Lifecycle

  ngOnDestroy() {
    if (this._filterSubscription) {
      this._filterSubscription.unsubscribe();
    }

    if (this._sortSubscription) {
      this._sortSubscription.unsubscribe();
    }

    if (this._sortStateSubscription) {
      this._sortStateSubscription.unsubscribe();
    }

    if (this._pageSizeSubscription) {
      this._pageSizeSubscription.unsubscribe();
    }

    if (this._pageIndexSubscription) {
      this._pageIndexSubscription.unsubscribe();
    }

    if (this._pageLoadSubscription) {
      this._pageLoadSubscription.unsubscribe();
    }

    if (this._countSubscription) {
      this._countSubscription.unsubscribe();
    }
    if (this._dataSubjectSubscription) {
      this._dataSubjectSubscription.unsubscribe();
    }
    
    if (this.scrollEvent$) {
      this.scrollEvent$.unsubscribe();
    }

    if (this._footerLoadingSubscription) {
      this._footerLoadingSubscription.unsubscribe();
    }

    if (this.scrollContainerId) {
      this.resetPaginator();
    }
  }
}
